import { useCallback, useState } from 'react';

import { ExclamationTriangleIcon } from '@radix-ui/react-icons';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  ConfirmModal,
  ConfirmModalProps,
} from 'src/common/modals/ConfirmModal';
import { show } from 'src/common/primitives/Toast/toast.slice';

import {
  dismissDispatchModal,
  useDispatchModalToShow,
} from '../dispatcher.slice';
import { useBulkUnassignTrips } from './useBulkUnassignTrips';

export const UnassignConfirmationModal = () => {
  const props = useUnassignConfirmationModalProps();
  return <ConfirmModal {...props} />;
};

// Extracted hook for testability since modals don't render in React root
export const useUnassignConfirmationModalProps = (): ConfirmModalProps => {
  const modalToShow = useDispatchModalToShow();
  const { onConfirmUnassign, dismissUnassignConfirmation, unassignError } =
    useUnassignConfirmation();
  return {
    open: modalToShow === 'unassign',
    setOpen: (isOpen) => {
      if (!isOpen) dismissUnassignConfirmation();
    },
    onCancel: dismissUnassignConfirmation,
    onConfirm: onConfirmUnassign,
    cancelText: 'Cancel',
    confirmText: 'Unassign',
    title: 'Unassign All Driver Trips',
    description:
      'Are you sure you want to unassign trips? Locked and in progress trips will not be unassigned.',
    error: unassignError,
    icon: <ExclamationTriangleIcon className="w-5" />,
    mode: 'destructive',
  };
};

export const useUnassignConfirmation = () => {
  const dispatch = useAppDispatch();
  const assignmentId = useAppSelector(
    (state) => state.dispatcher.modals.assignmentId
  );
  const [unassignError, setUnassignError] = useState<string | undefined>(
    undefined
  );

  const dismissUnassignConfirmation = useCallback(() => {
    dispatch(dismissDispatchModal());
    setUnassignError(undefined);
  }, [dispatch]);

  const { handleBulkUnassignTrips } = useBulkUnassignTrips();

  const handleUnassignAllTrips = useCallback(async () => {
    if (!assignmentId) {
      setUnassignError('Missing assignment ID');
      return;
    }

    const result = await handleBulkUnassignTrips([assignmentId]);

    if (result.failedCount > 0) {
      setUnassignError(result.errors[assignmentId]);
      return;
    }

    dispatch(
      show({
        title: 'Trips Unassigned',
        description: 'All trips have been unassigned',
        type: 'success',
      })
    );

    dismissUnassignConfirmation();
  }, [
    assignmentId,
    dispatch,
    dismissUnassignConfirmation,
    handleBulkUnassignTrips,
  ]);

  return {
    unassignError,
    dismissUnassignConfirmation,
    onConfirmUnassign: () => handleUnassignAllTrips(),
  };
};
